import { dynamicFieldSchema } from 'components/AssetForm/helpers/DynamicForm';
import { stringToNumber } from 'lib/number';
import z, { ZodError } from 'zod';

export const urlValidator = z.object({
    value: z.union([z.string().url({ message: 'Invalid url' }), z.null(), z.literal('')]),
});

export const categoryFormSchema = z
    .object({
        title: z.string().min(2, { message: 'Title must be at least 2 characters long' }),
        additional: z
            .object({
                description: z.string().optional().nullable(),
                images: z
                    .array(
                        z
                            .object({
                                key: z
                                    .string()
                                    .trim()
                                    .min(1, { message: 'Image name is required' })
                                    .regex(/^[a-zA-Z0-9\-_]{1,255}$/, { message: 'Invalid title' }),
                            })
                            .passthrough()
                    )
                    .optional(),
                metadata: z
                    .object({
                        podcast_author: z.object({ value: z.string().optional() }).optional(),
                        podcast_language: z.object({ value: z.string().optional() }).optional(),
                        podcast_link: urlValidator.optional(),
                        podcast_ownerEmail: z
                            .object({
                                value: z.union([
                                    z.string().email({ message: 'Invalid e-mail' }),
                                    z.null(),
                                    z.literal(''),
                                ]),
                            })
                            .optional(),
                        podcast_acast_prependUrl: urlValidator.optional(),
                        podcast_richDescription: z
                            .object({
                                value: z
                                    .union([
                                        z.literal(''),
                                        z
                                            .string()
                                            .min(2, { message: 'Rich description must be at least 2 characters long' })
                                            .max(2048, {
                                                message: 'Rich description must be at most 2048 characters long',
                                            }),
                                    ])
                                    .optional()
                                    .nullable(),
                            })
                            .optional(),
                        podcast_automaticAds: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^0|[1-4]\d*$/, { message: 'Should be a number between 0 and 4' })
                                    .optional()
                                    .nullable(),
                            })
                            .optional(),
                        podcast_customPrerollPosition: z
                            .object({
                                value: z.string().optional().nullable(),
                            })
                            .optional(),
                        podcast_customPostrollPosition: z
                            .object({
                                value: z.string().optional().nullable(),
                            })
                            .optional(),
                        podcast_numberOfPrerollSpons: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^0|[1-9]\d*$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                        podcast_numberOfPrerollAds: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^(0|[1-9]\d*)$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                        podcast_numberOfMidrollSpons: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^(0|[1-9]\d*)$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                        podcast_numberOfMidrollAds: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^(0|[1-9]\d*)$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                        podcast_numberOfPostrollSpons: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^(0|[1-9]\d*)$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                        podcast_numberOfPostrollAds: z
                            .object({
                                value: z
                                    .string()
                                    .regex(/^$|^(0|[1-9]\d*)$/, { message: 'Should be a number greater or equal to 0' })
                                    .optional(),
                            })
                            .optional(),
                    })
                    .passthrough()
                    .superRefine((metadata, ctx) => {
                        const automaticAds = stringToNumber(metadata.podcast_automaticAds?.value) || 0;
                        const customPrerollPosition = stringToNumber(metadata.podcast_customPrerollPosition?.value);
                        const customPostrollPosition = stringToNumber(metadata.podcast_customPostrollPosition?.value);
                        const maxRange = Math.min((1 / (Math.min(automaticAds, 4) + 1)) * 2, 0.5);
                        if (
                            (customPrerollPosition || customPrerollPosition === 0) &&
                            (customPrerollPosition < 0 || customPrerollPosition > maxRange)
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['podcast_customPrerollPosition.value'],
                                message: `Should be a value between 0% and ${Math.round(maxRange * 100)}%`,
                            });
                        }
                        if (
                            (customPostrollPosition || customPostrollPosition === 0) &&
                            (customPostrollPosition < 1 - maxRange || customPostrollPosition > 1)
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['podcast_customPostrollPosition.value'],
                                message: `Should be a value between ${Math.round((1 - maxRange) * 100)}% and 100%`,
                            });
                        }
                    }),
            })
            .passthrough(),
        simpleMetadata: z
            .array(
                z.object({
                    key: z.string().min(1, { message: 'Metadata key is required' }),
                    value: z.string().min(1, { message: 'Metadata value is required' }),
                })
            )
            .optional(),
        dynamicFieldsMetadata: z
            .array(
                z.object({
                    key: z.string().min(1, { message: 'Key is required' }),
                    value: z
                        .string()
                        .min(1, { message: 'Dynamic field config is required' })
                        .superRefine((value, ctx) => {
                            try {
                                dynamicFieldSchema.parse(JSON.parse(value));
                            } catch (error) {
                                if (error instanceof ZodError) {
                                    const issue = error.issues[0];
                                    if (issue) {
                                        ctx.addIssue({
                                            code: z.ZodIssueCode.custom,
                                            message: `Config schema error${
                                                issue.path?.length ? ` (path: '${issue.path.join('.')}')` : ''
                                            }: ${issue.message.toLowerCase()}`,
                                        });
                                    } else {
                                        ctx.addIssue({
                                            code: z.ZodIssueCode.custom,
                                            message: 'Unknown config schema error',
                                        });
                                    }
                                } else {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: 'Config is not valid JSON',
                                    });
                                }
                            }
                        }),
                })
            )
            .optional(),
        podmeMetadata: z
            .object({
                sendToPodme: z.boolean().optional(),
                sendRssToPodme: z.boolean().optional(),
                podmeCategories: z.array(z.number()).optional(),
            })
            .superRefine(({ sendToPodme, podmeCategories }, ctx) => {
                if (sendToPodme && !podmeCategories?.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['podmeCategories'],
                        message: 'Please choose category',
                    });
                }
            })
            .optional(),
    })
    .passthrough()
    .superRefine(({ additional, podmeMetadata }, ctx) => {
        const { description, image, metadata } = additional;
        const { sendToPodme } = podmeMetadata ?? {};
        if (!sendToPodme) {
            return;
        }
        if (!description) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['additional.description'],
                message: 'Description is required for Podme integration',
            });
        }
        if (!image) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['additional.image'],
                message: 'Image is required for Podme integration',
            });
        }
        if (!metadata.podcast_author?.value) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['additional.metadata.podcast_author.value'],
                message: 'Author is required for Podme integration',
            });
        }
        if (!metadata.podcast_language?.value) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['additional.metadata.podcast_language.value'],
                message: 'Language is required for Podme integration',
            });
        }
    });
